import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, Pressable, Icon3 } from '../lib/react-native-compat';
import { fNum, pNum, updateUserDataArray, deleteUserData } from '../db/company';

// Transient focus signal. Set by callers (e.g. ThirdPartyRow) before opening
// the popover so the modal can highlight the right field on mount.
// Module-level singleton so we don't have to plumb extra state through every pivot.
let _pendingFocusField = null;
export const setPendingFocusField = (field) => { _pendingFocusField = field; };
const consumePendingFocusField = () => { const f = _pendingFocusField; _pendingFocusField = null; return f; };

/**
 * EtcPopover — Shown when a tree node has multiple ETC entries at the same location.
 *
 * Allows the user to:
 *   - See every individual DB entry with its amount
 *   - Edit entry fields inline (level, phase, cost type, resource, PO, amount)
 *   - Delete an individual entry
 *   - Add a new entry at this level
 *
 * Props:
 *   entries     – Array<{ guid, amount, notes, description, level, Phase_Code, Supplier_Name, PO_Number, Cost_Type_Code, Resource_Code, _row }>
 *   childEntries – entries below this level
 *   breadcrumb  – The breadcrumb key for this node
 *   nodeData    – The tree node's data object (path, key, indent, etc.)
 *   drillColumns – Current drill order
 *   tabConfig   – { tab, defaultCostType, ... }
 *   selectedJob – Job ID
 *   selectedVersion – Version GUID
 *   lockedVersion – Whether version is locked
 *   onClose     – Callback to close the popover
 *   onSaved     – Callback after save (triggers data reload)
 *   onDeleted   – Callback after delete (triggers data reload)
 *   lookupOptions – { phases: {code: desc}, costTypes: {code: desc}, resources: {code: desc} }
 *   style       – Optional positioning styles
 */

const levelOptions = ['Discipline', 'Phase', 'Vendor', 'PO', 'Cost Type', 'Resource', 'Top Level'];

const levelColor = (lvl) => {
    const colors = { 'Discipline': '#0ea5e9', 'Phase': '#3b82f6', 'Vendor': '#8b5cf6', 'PO': '#f59e0b', 'Line': '#10b981', 'Cost Type': '#ef4444', 'User Row': '#6366f1', 'Top Level': '#64748b', 'Resource': '#059669' };
    return colors[lvl] || '#6b7280';
};

const selectStyle = {
    fontSize: 11, padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: 3,
    backgroundColor: '#fff', color: '#1e293b', cursor: 'pointer', width: '100%', height: 24,
};

const inputStyle = {
    fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3,
    padding: '2px 4px', height: 24, width: '100%',
};

const COL = { level: 80, title: 110, phase: 120, costType: 105, vendor: 110, resource: 120, po: 78, amount: 90, notes: 110, description: 130, actions: 28 };

const SearchableSelect = ({ value, onChange, options, style, placeholder = '--', dropdownWidth, allowCustom = false, invalid = false }) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [pos, setPos] = React.useState({ top: 0, left: 0 });
    const ref = React.useRef(null);
    const inputRef = React.useRef(null);
    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    React.useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
    const filtered = search
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options;
    const selected = options.find(o => o.value === value);
    const trimmedSearch = search.trim();
    const hasExactMatch = trimmedSearch !== '' && options.some(o =>
        o.label.toLowerCase() === trimmedSearch.toLowerCase() || ('' + o.value).toLowerCase() === trimmedSearch.toLowerCase()
    );
    const commitCustom = () => { onChange(trimmedSearch); setOpen(false); setSearch(''); };
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered.length === 1) { onChange(filtered[0].value); setOpen(false); setSearch(''); }
            else if (allowCustom && trimmedSearch !== '') commitCustom();
        } else if (e.key === 'Escape') {
            setOpen(false); setSearch('');
        }
    };
    const handleOpen = (e) => {
        e.stopPropagation();
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropUp = spaceBelow < 240 && rect.top > 240;
            setPos({
                top: dropUp ? rect.top : rect.bottom,
                left: rect.left,
                dropUp,
            });
        }
        setOpen(!open);
    };
    return (
        <div ref={ref} style={{ position: 'relative', width: '100%' }}>
            <div
                onClick={handleOpen}
                style={{ ...selectStyle, ...style, ...(invalid ? { border: '1px solid #dc2626', backgroundColor: '#fef2f2' } : {}), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '20px' }}
            >
                {selected ? selected.label : (value ? <span style={{ color: '#64748b' }}>{value}</span> : <span style={{ color: '#9ca3af' }}>{placeholder}</span>)}
            </div>
            {open && (
                <div style={{
                    position: 'fixed',
                    top: pos.dropUp ? undefined : pos.top,
                    bottom: pos.dropUp ? (window.innerHeight - pos.top) : undefined,
                    left: pos.left,
                    zIndex: 10000,
                    background: 'white', border: '1px solid #cbd5e1', borderRadius: 4,
                    width: dropdownWidth || 'max-content', minWidth: ref.current?.offsetWidth || 80, maxHeight: 220,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column',
                }}>
                    <input
                        ref={inputRef}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={allowCustom ? 'Search or type new...' : 'Search...'}
                        style={{ width: '100%', padding: '4px 6px', border: 'none', borderBottom: '1px solid #e2e8f0', fontSize: 11, outline: 'none', boxSizing: 'border-box' }}
                    />
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        <div
                            onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                            style={{ padding: '3px 6px', fontSize: 11, cursor: 'pointer', color: '#9ca3af' }}
                        >{placeholder}</div>
                        {allowCustom && trimmedSearch !== '' && !hasExactMatch && (
                            <div
                                onClick={commitCustom}
                                style={{ padding: '3px 6px', fontSize: 11, cursor: 'pointer', color: '#1d4ed8', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}
                            >+ Add "{trimmedSearch}"</div>
                        )}
                        {filtered.map(o => (
                            <div
                                key={o.value}
                                onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                                style={{
                                    padding: '3px 6px', fontSize: 11, cursor: 'pointer',
                                    backgroundColor: o.value === value ? '#eff6ff' : 'transparent',
                                    whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={e => { if (o.value !== value) e.target.style.backgroundColor = '#f1f5f9'; }}
                                onMouseLeave={e => { e.target.style.backgroundColor = o.value === value ? '#eff6ff' : 'transparent'; }}
                            >{o.label}</div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const EtcPopover = ({
    entries = [],
    childEntries = [],
    breadcrumb,
    nodeData,
    drillColumns,
    tabConfig,
    selectedJob,
    selectedVersion,
    lockedVersion,
    userData,
    setUserData,
    onClose,
    onSaved,
    onDeleted,
    lookupOptions = {},
}) => {
    // Local editable state per entry keyed by guid
    const [localEdits, setLocalEdits] = useState({});
    const [deletingGuids, setDeletingGuids] = useState(new Set());
    const hasNoEntries = entries.filter(e => e.amount !== 0 || e.description || e.notes).length === 0
        && childEntries.filter(e => e.amount !== 0 || e.description || e.notes).length === 0;
    const [addingNew, setAddingNew] = useState(hasNoEntries);
    const popoverRef = useRef(null);

    // ── Compute context defaults from nodeData + breadcrumb ──
    const reverseDisciplineMap = lookupOptions.reverseDisciplineMap || {};
    const disciplineMap = lookupOptions.disciplineMap || {};

    const contextDefaults = React.useMemo(() => {
        const defaults = { level: '', phase: '', costType: '', vendor: '', resource: '', po: '' };
        if (!nodeData || !drillColumns?.length) return defaults;
        // Walk the breadcrumb to extract values at each drill level
        const bcParts = (nodeData.breadcrumb || '').split('^').filter(s => s !== '');
        drillColumns.forEach((col, i) => {
            if (i < bcParts.length) {
                const val = bcParts[i];
                if (col === 'Phase_Code') defaults.phase = val;
                else if (col === 'Cost_Type_Code') defaults.costType = val;
                else if (col === 'Supplier_Name') defaults.vendor = val;
                else if (col === 'Resource_Code') defaults.resource = val;
                else if (col === 'PO_Number') defaults.po = val;
            }
        });
        // Derive level label from the node's path
        const pathToLevel = { 'Phase_Code': 'Phase', 'Supplier_Name': 'Vendor', 'PO_Number': 'PO', 'Cost_Type_Code': 'Cost Type', 'Discipline': 'Discipline', 'Resource_Code': 'Resource' };
        defaults.level = pathToLevel[nodeData.path] || 'Phase';
        // If discipline is in the breadcrumb but phase is not, default phase to the first in that discipline
        const discIdx = drillColumns.indexOf('Discipline');
        if (discIdx >= 0 && discIdx < bcParts.length && !defaults.phase) {
            const discName = bcParts[discIdx];
            const discPhases = reverseDisciplineMap[discName] || [];
            if (discPhases.length > 0) defaults.phase = discPhases[0];
        }
        // Default cost type from tabConfig
        if (!defaults.costType) defaults.costType = tabConfig.defaultCostType || '';
        return defaults;
    }, [nodeData, drillColumns, reverseDisciplineMap, tabConfig.defaultCostType]);

    const [newEntry, setNewEntry] = useState({ ...contextDefaults, amount: '', notes: '', description: '', title: '' });

    // Update newEntry defaults when contextDefaults change (e.g. popover opens on different node)
    useEffect(() => {
        if (!addingNew) {
            setNewEntry({ ...contextDefaults, amount: '', notes: '', description: '', title: '' });
        }
    }, [contextDefaults]);

    // ── Compute relevant context for sorting dropdowns ──
    const contextDiscipline = React.useMemo(() => {
        const discIdx = drillColumns?.indexOf('Discipline');
        if (discIdx >= 0) {
            const bcParts = (nodeData?.breadcrumb || '').split('^').filter(s => s !== '');
            if (discIdx < bcParts.length) return bcParts[discIdx];
        }
        return '';
    }, [nodeData, drillColumns]);

    // Phases in the current discipline (for sorting to top)
    const contextPhases = React.useMemo(() => {
        if (!contextDiscipline) return new Set();
        return new Set(reverseDisciplineMap[contextDiscipline] || []);
    }, [contextDiscipline, reverseDisciplineMap]);

    // Vendors/POs from entries at this node (for sorting to top)
    const contextVendors = React.useMemo(() => {
        const set = new Set();
        [...entries, ...childEntries].forEach(e => { if (e.Supplier_Name) set.add(e.Supplier_Name); });
        if (nodeData?.detail?.Supplier_Name) set.add(nodeData.detail.Supplier_Name);
        return set;
    }, [entries, childEntries, nodeData]);

    const contextPOs = React.useMemo(() => {
        const set = new Set();
        [...entries, ...childEntries].forEach(e => { if (e.PO_Number) set.add('' + e.PO_Number); });
        if (nodeData?.detail?.PO_Number) set.add('' + nodeData.detail.PO_Number);
        return set;
    }, [entries, childEntries, nodeData]);

    // Sort helper: relevant items first, then alphabetical
    const sortWithContext = (arr, contextSet) => {
        if (!contextSet || contextSet.size === 0) return arr;
        const inCtx = arr.filter(v => contextSet.has(v));
        const rest = arr.filter(v => !contextSet.has(v));
        return [...inCtx, ...rest];
    };

    // Refs carry latest state/fn so the outside-click handler (bound once) uses them
    const newEntryRef = useRef(newEntry);
    useEffect(() => { newEntryRef.current = newEntry; }, [newEntry]);
    const addingNewRef = useRef(addingNew);
    useEffect(() => { addingNewRef.current = addingNew; }, [addingNew]);
    const handleAddNewRef = useRef(null);

    // Close on outside click — auto-save pending new entry if it has any content
    const hasPendingContent = (ne) => {
        if (!ne) return false;
        if (ne.amount && pNum(ne.amount) !== 0) return true;
        return !!((ne.notes || '').trim() || (ne.description || '').trim() || (ne.title || '').trim());
    };
    useEffect(() => {
        const handler = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                if (addingNewRef.current && hasPendingContent(newEntryRef.current)) {
                    handleAddNewRef.current?.();
                }
                onClose?.();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    // Explicit close (X button) — also auto-save pending new entry.
    const handleClose = () => {
        if (addingNew && hasPendingContent(newEntry)) {
            handleAddNewRef.current?.();
        }
        onClose?.();
    };

    // Initialize local edits from entries — but ONLY for guids we haven't
    // seen yet. The parent re-renders this modal whenever userData changes
    // (e.g. after each save in this same modal), which would otherwise
    // clobber the user's in-flight typed-but-not-yet-saved edits.
    const initializedGuidsRef = useRef(new Set());
    // Reset the seen-guids set when the popover switches to a different
    // breadcrumb so re-opening syncs fresh values from the server.
    useEffect(() => {
        initializedGuidsRef.current = new Set();
        setLocalEdits({});
    }, [breadcrumb]);
    useEffect(() => {
        const newEdits = {};
        let added = false;
        [...entries, ...childEntries].forEach(e => {
            if (!e.guid || initializedGuidsRef.current.has(e.guid)) return;
            initializedGuidsRef.current.add(e.guid);
            newEdits[e.guid] = {
                level: e.level || '',
                Phase_Code: e.Phase_Code || '',
                Cost_Type_Code: e.Cost_Type_Code || '',
                Supplier_Name: e.Supplier_Name || '',
                Resource_Code: e.Resource_Code || '',
                PO_Number: e.PO_Number || '',
                amount: e.amount + '',
                notes: e.notes || '',
                description: e.description || '',
                title: e.title || '',
            };
            added = true;
        });
        if (added) setLocalEdits(prev => ({ ...prev, ...newEdits }));
    }, [entries, childEntries]);

    const total = entries.reduce((s, e) => s + e.amount, 0);
    const visibleChildEntries = childEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const childTotal = visibleChildEntries.reduce((s, e) => s + e.amount, 0);
    const grandTotal = total + childTotal;
    const visibleEntries = entries.filter(e => e.amount !== 0 || e.description || e.notes);
    const visibleCount = visibleEntries.length + visibleChildEntries.length;

    // Defer heavy list mount so the modal shell + spinner paint first.
    // Each entry row mounts 5 SearchableSelect dropdowns; rendering 65 rows
    // synchronously stalls the click-to-open.
    const [listReady, setListReady] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setListReady(true), 30);
        return () => clearTimeout(id);
    }, []);
    // Pagination: only render PAGE rows at a time; user can "Show more".
    const PAGE = 25;
    const [shownSelf, setShownSelf] = useState(PAGE);
    const [shownChild, setShownChild] = useState(PAGE);

    const phases = lookupOptions.phases || {};
    const costTypes = lookupOptions.costTypes || {};
    const resources = lookupOptions.resources || {};
    const vendors = lookupOptions.vendors || [];
    const pos = lookupOptions.pos || [];

    // Sorted phase keys: context-relevant phases first
    const sortedPhaseKeys = React.useMemo(() => {
        const keys = Object.keys(phases);
        if (contextPhases.size === 0) return keys;
        const inCtx = keys.filter(k => contextPhases.has(k));
        const rest = keys.filter(k => !contextPhases.has(k));
        return [...inCtx, ...rest];
    }, [phases, contextPhases]);

    const sortedVendors = React.useMemo(() => sortWithContext(vendors, contextVendors), [vendors, contextVendors]);
    const sortedPOs = React.useMemo(() => sortWithContext(pos, contextPOs), [pos, contextPOs]);

    const phaseResourceMap = lookupOptions.phaseResourceMap || {};
    const budgetPhaseSet = React.useMemo(() => {
        const bp = lookupOptions.budgetPhases || [];
        return bp.length > 0 ? new Set(bp) : null;
    }, [lookupOptions.budgetPhases]);

    // Memoized option arrays for SearchableSelect
    const levelOpts = React.useMemo(() => levelOptions.map(l => ({ value: l, label: l })), []);
    const phaseOpts = React.useMemo(() => {
        const keys = budgetPhaseSet
            ? sortedPhaseKeys.filter(code => budgetPhaseSet.has(code))
            : sortedPhaseKeys;
        return keys.map(code => ({
            value: code, label: code + (phases[code] ? ' - ' + phases[code] : ''),
        }));
    }, [sortedPhaseKeys, phases, budgetPhaseSet]);
    const costTypeOpts = React.useMemo(() => {
        return Object.entries(costTypes).map(([code, desc]) => ({ value: code, label: code + (desc ? ' - ' + desc : '') }));
    }, [costTypes]);
    const vendorOpts = React.useMemo(() => sortedVendors.map(v => ({ value: v, label: v })), [sortedVendors]);
    const codesByCostType = lookupOptions.codesByCostType || null;
    const resourceOptsAll = React.useMemo(() => {
        // Show only the description in the dropdown; fall back to the code when
        // a resource has no description on file.
        return Object.entries(resources).map(([code, desc]) => ({ value: code, label: desc ? desc : code }));
    }, [resources]);
    const resourceOptsCache = React.useRef({});
    React.useEffect(() => { resourceOptsCache.current = {}; }, [resources, codesByCostType]);
    const getResourceOpts = React.useCallback((costType) => {
        if (!costType || !codesByCostType) return resourceOptsAll;
        const allowed = codesByCostType[costType];
        if (!allowed || Object.keys(allowed).length === 0) return resourceOptsAll;
        if (resourceOptsCache.current[costType]) return resourceOptsCache.current[costType];
        const out = resourceOptsAll.filter(o => allowed[o.value] != null);
        resourceOptsCache.current[costType] = out.length ? out : resourceOptsAll;
        return resourceOptsCache.current[costType];
    }, [resourceOptsAll, codesByCostType]);
    const poOpts = React.useMemo(() => sortedPOs.map(p => ({ value: p, label: p })), [sortedPOs]);

    const getLocal = (guid, field) => localEdits[guid]?.[field] ?? '';

    const updateLocal = (guid, field, value) => {
        setLocalEdits(prev => ({ ...prev, [guid]: { ...prev[guid], [field]: value } }));
    };

    // Save entry with optional field overrides (for immediate dropdown saves)
    const saveEntry = (entry, overrides = {}) => {
        const local = { ...(localEdits[entry.guid] || {}), ...overrides };
        const data = {
            active: 1,
            userId: '154045',
            updateRow: 1,
            guidId: entry.guid + '',
            userName: 'Seth Bailey',
            fieldName: 'etc',
            tab: tabConfig.tab,
            rowId: entry._row?.Forecast_Row_ID || breadcrumb,
            version: selectedVersion + '',
            value: (local.amount !== undefined ? local.amount : entry.amount) + '',
            id: selectedJob,
            costType: local.Cost_Type_Code || entry.Cost_Type_Code || tabConfig.defaultCostType,
            costCode: local.Phase_Code || entry.Phase_Code || '',
            vendor: (local.Supplier_Name !== undefined ? local.Supplier_Name : entry.Supplier_Name) || '',
            po: (local.PO_Number || entry.PO_Number || '').toString().substring(0, 10),
            equipment: local.level || entry.level || '',
            lockedVersion,
            valueAmount: pNum(local.amount !== undefined ? local.amount : entry.amount),
            description: (local.description !== undefined ? local.description : entry.description) || '',
            notes: (local.notes !== undefined ? local.notes : entry.notes) || '',
            resourceCode: local.Resource_Code || entry.Resource_Code || '',
            rowLabel: (local.title !== undefined ? local.title : entry.title) || '',
        };
        updateUserDataArray(data);
        // Update atom state
        const stateUpdate = {
            ...entry._row,
            Value_Amount: pNum(local.amount !== undefined ? local.amount : entry.amount),
            Forecast_Value: (local.amount !== undefined ? local.amount : entry.amount) + '',
            Cost_Code: local.Phase_Code || entry.Phase_Code || '',
            Cost_Type: local.Cost_Type_Code || entry.Cost_Type_Code || '',
            Supplier_Name: (local.Supplier_Name !== undefined ? local.Supplier_Name : entry.Supplier_Name) || '',
            PO_Number: local.PO_Number || entry.PO_Number || '',
            Equipment_Tier: local.level || entry.level || '',
            Resource_Code: local.Resource_Code || entry.Resource_Code || '',
            Notes: (local.notes !== undefined ? local.notes : entry.notes) || '',
            Description: (local.description !== undefined ? local.description : entry.description) || '',
            Row_Label: (local.title !== undefined ? local.title : entry.title) || '',
        };
        setUserData(prev => {
            const filtered = prev.filter(
                item => !(item.Forecast_User_Data_GUID === entry.guid && item.Forecast_Tab === tabConfig.tab)
            );
            filtered.push(stateUpdate);
            return filtered;
        });
    };

    const handleFieldChange = (entry, field, value) => {
        updateLocal(entry.guid, field, value);
        const overrides = { [field]: value };
        // Auto-fill resource when phase changes and resource is empty
        if (field === 'Phase_Code' && value && phaseResourceMap[value] && !getLocal(entry.guid, 'Resource_Code')) {
            updateLocal(entry.guid, 'Resource_Code', phaseResourceMap[value]);
            overrides.Resource_Code = phaseResourceMap[value];
        }
        saveEntry(entry, overrides);
    };

    const handleAmountBlur = (entry) => {
        const local = localEdits[entry.guid];
        if (local && local.amount !== undefined && pNum(local.amount) !== entry.amount) {
            saveEntry(entry);
        }
    };

    const handleNotesBlur = (entry) => {
        const local = localEdits[entry.guid];
        const origNotes = entry.notes || '';
        const origDesc = entry.description || '';
        const origTitle = entry.title || '';
        if (local && (
            (local.notes !== undefined && local.notes !== origNotes) ||
            (local.description !== undefined && local.description !== origDesc) ||
            (local.title !== undefined && local.title !== origTitle)
        )) {
            saveEntry(entry);
        }
    };

    const handleDelete = (entry) => {
        setDeletingGuids(prev => new Set(prev).add(entry.guid));
        setTimeout(() => {
            deleteUserData({
                id: selectedJob,
                forecastGuid: selectedVersion,
                lockedVersion,
                rowId: entry.guid + '',
                tab: tabConfig.tab,
            });
            setUserData(prev => prev.filter(
                item => !(item.Forecast_User_Data_GUID === entry.guid && item.Forecast_Tab === tabConfig.tab)
            ));
            setDeletingGuids(prev => { const s = new Set(prev); s.delete(entry.guid); return s; });
            onDeleted?.();
        }, 300);
    };

    const handleAddNew = () => {
        const hasAmount = newEntry.amount && pNum(newEntry.amount) !== 0;
        const hasContent = (newEntry.notes || '').trim() || (newEntry.description || '').trim() || (newEntry.title || '').trim();
        if (!hasAmount && !hasContent) return;
        const ref = entries[0] || {};
        const data = {
            active: 1,
            userId: '154045',
            userName: 'Seth Bailey',
            fieldName: 'etc',
            tab: tabConfig.tab,
            rowId: breadcrumb + '^~u.' + Math.random().toString(16).substring(2, 8),
            version: selectedVersion + '',
            value: newEntry.amount + '',
            id: selectedJob,
            costType: newEntry.costType || ref.Cost_Type_Code || tabConfig.defaultCostType,
            costCode: newEntry.phase || ref.Phase_Code || '',
            vendor: newEntry.vendor || ref.Supplier_Name || '',
            po: (newEntry.po || '').toString().substring(0, 10),
            equipment: newEntry.level || '',
            lockedVersion,
            valueAmount: pNum(newEntry.amount),
            description: newEntry.description || '',
            notes: newEntry.notes || '',
            resourceCode: newEntry.resource || '',
            rowLabel: newEntry.title || '',
        };
        updateUserDataArray(data);
        const stateUpdate = {
            Forecast_Field_Name: 'etc',
            Forecast_Value: newEntry.amount + '',
            Forecast_Row_ID: data.rowId,
            Forecast_Tab: tabConfig.tab,
            BU_ID: selectedJob,
            Cost_Code: newEntry.phase || ref.Phase_Code || '',
            Cost_Type: newEntry.costType || ref.Cost_Type_Code || tabConfig.defaultCostType,
            Supplier_Name: newEntry.vendor || ref.Supplier_Name || '',
            PO_Number: newEntry.po || '',
            Equipment_Tier: newEntry.level || '',
            Value_Amount: pNum(newEntry.amount),
            Notes: newEntry.notes || '',
            Description: newEntry.description || '',
            Resource_Code: newEntry.resource || '',
            Row_Label: newEntry.title || '',
        };
        setUserData(prev => [...prev, stateUpdate]);
        setAddingNew(false);
        setNewEntry({ ...contextDefaults, amount: '', notes: '', description: '', title: '' });
        onSaved?.();
    };
    handleAddNewRef.current = handleAddNew;

    // Shared row renderer for both "at this level" and "below this level" entries
    const renderEntryRow = (entry, idx, isChild) => {
        const isDeleting = deletingGuids.has(entry.guid);
        return (
            <View key={(isChild ? 'child-' : '') + (entry.guid || idx)} style={{
                flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingHorizontal: 4, gap: 4,
                backgroundColor: isDeleting ? '#fee2e2' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                opacity: isDeleting ? 0.5 : 1, transition: 'opacity 0.3s',
                borderBottom: '1px solid #e2e8f0',
            }}>
                {/* Title */}
                <View style={{ width: COL.title }}>
                    <input
                        value={getLocal(entry.guid, 'title')}
                        onChange={(e) => updateLocal(entry.guid, 'title', e.target.value)}
                        onBlur={() => handleNotesBlur(entry)}
                        placeholder="Title"
                        style={{ width: '100%', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, padding: '2px 4px', height: 24, color: '#1e293b', boxSizing: 'border-box', outline: 'none', fontWeight: '600' }}
                    />
                </View>
                {/* Phase */}
                <View style={{ width: COL.phase }}>
                    <SearchableSelect
                        value={getLocal(entry.guid, 'Phase_Code')}
                        onChange={(v) => handleFieldChange(entry, 'Phase_Code', v)}
                        options={phaseOpts}
                        dropdownWidth={280}
                    />
                </View>
                {/* Cost Type */}
                <View style={{ width: COL.costType }}>
                    <SearchableSelect
                        value={getLocal(entry.guid, 'Cost_Type_Code')}
                        onChange={(v) => handleFieldChange(entry, 'Cost_Type_Code', v)}
                        options={costTypeOpts}
                        dropdownWidth={250}
                    />
                </View>
                {/* Vendor */}
                <View style={{ width: COL.vendor }}>
                    <SearchableSelect
                        value={getLocal(entry.guid, 'Supplier_Name')}
                        onChange={(v) => handleFieldChange(entry, 'Supplier_Name', v)}
                        options={vendorOpts}
                        dropdownWidth={220}
                        allowCustom
                    />
                </View>
                {/* Resource */}
                <View style={{ width: COL.resource }}>
                    <SearchableSelect
                        value={getLocal(entry.guid, 'Resource_Code')}
                        onChange={(v) => handleFieldChange(entry, 'Resource_Code', v)}
                        options={getResourceOpts(getLocal(entry.guid, 'Cost_Type_Code') || entry.Cost_Type_Code)}
                        dropdownWidth={220}
                    />
                </View>
                {/* PO */}
                <View style={{ width: COL.po }}>
                    <SearchableSelect
                        value={getLocal(entry.guid, 'PO_Number')}
                        onChange={(v) => handleFieldChange(entry, 'PO_Number', v)}
                        options={poOpts}
                    />
                </View>
                {/* Amount */}
                <View style={{ width: COL.amount }}>
                    <TextInput
                        value={getLocal(entry.guid, 'amount')}
                        onChangeText={(v) => updateLocal(entry.guid, 'amount', v)}
                        onBlur={() => handleAmountBlur(entry)}
                        onKeyPress={(e) => { if (e.nativeEvent.key === 'Enter') handleAmountBlur(entry); }}
                        style={{ ...inputStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '600' }}
                    />
                </View>
                {/* Notes */}
                <View style={{ width: COL.notes }}>
                    <input
                        value={getLocal(entry.guid, 'notes')}
                        onChange={(e) => updateLocal(entry.guid, 'notes', e.target.value)}
                        onBlur={() => handleNotesBlur(entry)}
                        placeholder="Notes"
                        style={{ width: '100%', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, padding: '2px 4px', height: 24, color: '#1e293b', boxSizing: 'border-box', outline: 'none' }}
                    />
                </View>
                {/* Description */}
                <View style={{ width: COL.description }}>
                    <input
                        value={getLocal(entry.guid, 'description')}
                        onChange={(e) => updateLocal(entry.guid, 'description', e.target.value)}
                        onBlur={() => handleNotesBlur(entry)}
                        placeholder="Description"
                        style={{ width: '100%', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, padding: '2px 4px', height: 24, color: '#1e293b', boxSizing: 'border-box', outline: 'none' }}
                    />
                </View>
                {/* Delete */}
                <View style={{ width: COL.actions, alignItems: 'center' }}>
                    <Pressable onPress={() => !isDeleting && handleDelete(entry)} style={{ cursor: isDeleting ? 'default' : 'pointer' }}>
                        {isDeleting
                            ? <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(0,0,0,0.1)', borderTop: '2px solid #dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            : <Icon3 name="MdDeleteOutline" size={14} color="#dc2626" />
                        }
                    </Pressable>
                </View>
            </View>
        );
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <View ref={popoverRef} style={[{
            backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: 8,
            padding: 12, minWidth: 1180, maxWidth: 1280,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)',
            maxHeight: '80vh', overflowY: 'auto',
        }]}>
            {/* Header */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>ETC Entries ({visibleCount}) <Text style={{ fontWeight: '500', color: '#64748b', fontSize: 12 }}>— {nodeData?.key || breadcrumb}</Text></Text>
                <Pressable onPress={handleClose} style={{ cursor: 'pointer', padding: 4 }}>
                    <Icon3 name="MdClose" size={18} color="#9ca3af" />
                </Pressable>
            </View>

            {/* Column headers */}
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 3, paddingHorizontal: 4, paddingLeft: 19, gap: 4, borderBottom: '2px solid #e2e8f0', marginBottom: 2 }}>
                <Text style={{ width: COL.title, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Title</Text>
                <Text style={{ width: COL.phase, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Phase</Text>
                <Text style={{ width: COL.costType, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Cost Type</Text>
                <Text style={{ width: COL.vendor, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Vendor</Text>
                <Text style={{ width: COL.resource, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Resource</Text>
                <Text style={{ width: COL.po, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>PO</Text>
                <Text style={{ width: COL.amount, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Amount</Text>
                <Text style={{ width: COL.notes, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Notes</Text>
                <Text style={{ width: COL.description, fontSize: 9, fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Description</Text>
                <Text style={{ width: COL.actions }}></Text>
            </View>

            {visibleChildEntries.length > 0 && (
                <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748b', marginBottom: 2, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>At this level</Text>
            )}

            {!listReady ? (
                <View style={{ paddingVertical: 40, alignItems: 'center', justifyContent: 'center' }}>
                    <View style={{
                        width: 28, height: 28, borderRadius: 14,
                        border: '3px solid #e5e7eb', borderTopColor: '#3b82f6',
                        animation: 'etcSpin 0.8s linear infinite',
                    }} />
                    <style>{`@keyframes etcSpin { to { transform: rotate(360deg); } }`}</style>
                    <Text style={{ marginTop: 10, fontSize: 11, color: '#64748b' }}>Loading {visibleCount} entries…</Text>
                </View>
            ) : (
                <>
                {/* Entry list */}
                <View style={{ paddingLeft: 15 }}>
                {visibleEntries.slice(0, shownSelf).map((entry, idx) => renderEntryRow(entry, idx, false))}
                </View>
                {visibleEntries.length > shownSelf && (
                    <Pressable onPress={() => setShownSelf(n => n + PAGE)} style={{ cursor: 'pointer', paddingVertical: 4, paddingHorizontal: 15 }}>
                        <Text style={{ fontSize: 11, fontWeight: '600', color: '#3b82f6' }}>Show more ({visibleEntries.length - shownSelf} remaining)</Text>
                    </Pressable>
                )}

                {/* Child entries — below this level */}
                {visibleChildEntries.length > 0 && (
                    <>
                        <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748b', marginTop: 6, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>Below this level</Text>
                        <View style={{ paddingLeft: 15 }}>
                        {visibleChildEntries.slice(0, shownChild).map((entry, idx) => renderEntryRow(entry, idx, true))}
                        </View>
                        {visibleChildEntries.length > shownChild && (
                            <Pressable onPress={() => setShownChild(n => n + PAGE)} style={{ cursor: 'pointer', paddingVertical: 4, paddingHorizontal: 15 }}>
                                <Text style={{ fontSize: 11, fontWeight: '600', color: '#3b82f6' }}>Show more ({visibleChildEntries.length - shownChild} remaining)</Text>
                            </Pressable>
                        )}
                    </>
                )}
                </>
            )}

            {/* Total row */}
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingHorizontal: 4, borderTop: '2px solid #e2e8f0', marginTop: 4 }}>
                <Text style={{ flex: 1, fontSize: 13, fontWeight: '700', color: '#334155' }}>{visibleChildEntries.length > 0 ? 'Grand Total' : 'Total'}</Text>
                <Text style={{ fontSize: 14, fontWeight: '800', color: '#1d4ed8', fontFamily: 'monospace', paddingRight: COL.actions + 4 }}>{fNum(grandTotal)}</Text>
            </View>

            {/* Add new entry */}
            {addingNew ? (
                <View style={{ paddingTop: 6, borderTop: '1px solid #e5e7eb', marginTop: 6 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 4 }}>
                        {/* Title */}
                        <View style={{ width: COL.title }}>
                            <input
                                value={newEntry.title || ''}
                                onChange={(e) => setNewEntry(p => ({ ...p, title: e.target.value }))}
                                placeholder="Title"
                                style={{ width: '100%', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, padding: '2px 4px', height: 24, color: '#1e293b', boxSizing: 'border-box', outline: 'none', fontWeight: '600' }}
                            />
                        </View>
                        {/* Phase */}
                        <View style={{ width: COL.phase }}>
                            <SearchableSelect
                                value={newEntry.phase}
                                onChange={(v) => {
                                    const updates = { phase: v };
                                    if (v && phaseResourceMap[v] && !newEntry.resource) updates.resource = phaseResourceMap[v];
                                    setNewEntry(p => ({ ...p, ...updates }));
                                }}
                                options={phaseOpts}
                                dropdownWidth={280}
                            />
                        </View>
                        {/* Cost Type */}
                        <View style={{ width: COL.costType }}>
                            <SearchableSelect
                                value={newEntry.costType}
                                onChange={(v) => setNewEntry(p => ({ ...p, costType: v }))}
                                options={costTypeOpts}
                                dropdownWidth={250}
                            />
                        </View>
                        {/* Vendor */}
                        <View style={{ width: COL.vendor }}>
                            <SearchableSelect
                                value={newEntry.vendor}
                                onChange={(v) => setNewEntry(p => ({ ...p, vendor: v }))}
                                options={vendorOpts}
                                dropdownWidth={220}
                                allowCustom
                            />
                        </View>
                        {/* Resource */}
                        <View style={{ width: COL.resource }}>
                            <SearchableSelect
                                value={newEntry.resource}
                                onChange={(v) => setNewEntry(p => ({ ...p, resource: v }))}
                                options={getResourceOpts(newEntry.costType)}
                                dropdownWidth={220}
                            />
                        </View>
                        {/* PO */}
                        <View style={{ width: COL.po }}>
                            <SearchableSelect
                                value={newEntry.po}
                                onChange={(v) => setNewEntry(p => ({ ...p, po: v }))}
                                options={poOpts}
                            />
                        </View>
                        {/* Amount */}
                        <View style={{ width: COL.amount }}>
                            <TextInput
                                autoFocus
                                value={newEntry.amount}
                                onChangeText={(v) => setNewEntry(p => ({ ...p, amount: v }))}
                                onKeyPress={(e) => { if (e.nativeEvent.key === 'Enter') handleAddNew(); }}
                                style={{ ...inputStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '600' }}
                                placeholder="0.00"
                            />
                        </View>
                        {/* Notes */}
                        <View style={{ width: COL.notes }}>
                            <input
                                value={newEntry.notes || ''}
                                onChange={(e) => setNewEntry(p => ({ ...p, notes: e.target.value }))}
                                placeholder="Notes"
                                style={{ width: '100%', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, padding: '2px 4px', height: 24, color: '#1e293b', boxSizing: 'border-box', outline: 'none' }}
                            />
                        </View>
                        {/* Description */}
                        <View style={{ width: COL.description }}>
                            <input
                                value={newEntry.description || ''}
                                onChange={(e) => setNewEntry(p => ({ ...p, description: e.target.value }))}
                                placeholder="Description"
                                style={{ width: '100%', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, padding: '2px 4px', height: 24, color: '#1e293b', boxSizing: 'border-box', outline: 'none' }}
                            />
                        </View>
                        {/* Actions */}
                        <View style={{ width: COL.actions, flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                            <Pressable onPress={handleAddNew} style={{ cursor: 'pointer' }}>
                                <Icon3 name="MdCheck" size={16} color="#22c55e" />
                            </Pressable>
                            <Pressable onPress={() => { setAddingNew(false); setNewEntry({ ...contextDefaults, amount: '', notes: '', description: '', title: '' }); }} style={{ cursor: 'pointer' }}>
                                <Icon3 name="MdClose" size={14} color="#9ca3af" />
                            </Pressable>
                        </View>
                    </View>
                </View>
            ) : (
                <Pressable onPress={() => setAddingNew(true)} style={{ cursor: 'pointer', flexDirection: 'row', alignItems: 'center', gap: 4, paddingTop: 6, borderTop: '1px solid #e5e7eb', marginTop: 4 }}>
                    <Icon3 name="MdAdd" size={14} color="#3b82f6" />
                    <Text style={{ fontSize: 11, fontWeight: '600', color: '#3b82f6' }}>Add entry at this level</Text>
                </Pressable>
            )}
        </View>
        </div>
    );
};

export default EtcPopover;
